// BRIEF 86 PHASE 2, STEP 3 (run by brief 90 §5) — the Case info pane, once.
//
// 403 lines in `CaseView`, 375 in `CollectiveCaseView`. §1's run-shape question
// was "one component, or a shell with slots?" and the honest answer, reported in
// `docs/90-phase2-rulings-owed.md`, was NEITHER: the divergence was five
// BEHAVIOURS, not content in fixed positions. Once those were ruled and fixed —
// the prescription ladder, click-to-call, the jurisdiction link, the "no
// prescription" prefill, the status pill, `_caseFixed` on the edit path and the
// "+ Add" migration — what was left was the exempt class alone, and the exempt
// class is one component.
//
// EXTRACTED, NOT REWRITTEN (rule 3). The body below is CaseView's block lifted
// verbatim and de-indented. The ONLY edits are five parameterisations, each a
// container discriminator or a host handle:
//
//   the PRE-event lookup   a.caId  vs  a.ccId
//   the agenda modal       om()  vs  globalOm(), taken as `openAgendaModal`
//   the stages anchor      "ca-stages"  vs  "cc-stages"
//   the documents section  kind="case"  vs  "cc"
//   the financial gate     one prop — CCV's `canSeeFin` is literally
//                          `const canSeeFin = canFin` (CollectiveCaseView.tsx:2591)
//
// Everything else that used to differ was a free-name rename — `c` vs `c2`,
// `diff` vs `diff5`, seven of them — avoiding shadowing in a 10k-line file. One
// definition has one set of names, so they stop existing rather than being
// reconciled.
import { ChevronDown, ChevronRight } from "lucide-react";
import CallAvatar from "./CallAvatar";
import CaseDocumentsSection from "./CaseDocumentsSection";
import { ArchivedHoleBadge } from "./SharedUI";
import { fd, getLatestRegStage } from "../utils";
import { soleArchived } from "../lib/collaboratorState";
import { teamWithRpFirst, RP_BADGE_STYLE } from "../lib/teamOrder";
import type { RankedTeamEntry } from "../lib/teamOrder";
import type { Collaborator } from "../../shared/entities";
import CaseFinancialPanel from "./CaseFinancialPanel";
import { resolveTaxonomyLabel } from "../lib/taxonomyHelpers";
import { CT, FS } from "../lib/tokens";

// The read-sets this pane uses, named once. They are deliberately partial: this
// is a display pane and it reads a handful of fields off rows that carry dozens.
// The real row, not an invented shape: InitialsBadge/CallAvatar take a
// Collaborator and teamWithRpFirst returns RankedTeamEntry.
type Person = Collaborator;
type Matter = {
  id: number;
  ref?: string | null;
  status?: string | null;
  isProspect?: boolean;
  juId?: number | null;
  openDate?: string | null;
  pendingDate?: string | null;
  closedDate?: string | null;
  archivedDate?: string | null;
  introductionDate?: string | null;
  responsiblePartners?: number[];
  caseCollaborators?: { coId: number }[];
  stages?: unknown;
  [k: string]: unknown;
};
type Jurisdiction = { id: number; name?: string | null };
// Lifted out of the className template, where it was an inline object literal
// needing a cast to be indexed at all.
//
// A FUNCTION and not a Record<string, string>: i18n-total's `prose-map` arm
// reads a string→string map of literals as user-facing prose owing translation,
// and it is right to — that shape is almost always a label table. These are CSS
// class names, and the gate caught my first version saying otherwise.
// 141 §2.3 exported this for the client record's matter card; the 2026-08-27
// muting correction took the card off the four classes the same day (a status
// INHERITED from the matter renders muted there, in the absence pair, so it
// cannot be read as a status the client owns) — internal again, no second
// consumer left to disagree with.
function statusClass(v: unknown): string {
  const k = String(v ?? "");
  if (k === "pending" || k === "closed" || k === "archived") return `status-${k}`;
  return "status-active";
}
type AgendaRow = { caId?: number | null; ccId?: number | null; type?: string | null; date?: string | null };

type Props = {
  kind: "case" | "cc";
  cs: Matter;
  co: Person[];
  currentUser?: number | null;
  ag: AgendaRow[];
  ju: Jurisdiction[];
  canFin?: boolean;
  canTerms?: boolean;
  isRp?: boolean;
  _caseStatusEntries?: unknown;
  onCallColleague?: (p: Person) => void;
  callActive?: boolean;
  openAgendaModal: (kind: string, payload: Record<string, unknown>) => void;
  nav: (kind: string, id: number) => void;
  sCol: (k: string) => boolean;
  tog: (k: string) => void;
  t: (k: string, o?: Record<string, unknown>) => string;
  // The financial pane the section ends with mounts these.
  te: unknown[];
  inv: readonly { id?: number | null; vatRate?: number | null }[];
};

export default function CaseInfoSection({
  kind,
  cs,
  co,
  currentUser,
  ag,
  ju,
  canFin,
  canTerms,
  isRp,
  _caseStatusEntries,
  onCallColleague,
  callActive,
  openAgendaModal,
  nav,
  sCol,
  tog,
  t,
  te,
  inv,
}: Props) {
  const isCc = kind === "cc";
  const jr = ju.find((j) => j.id === cs.juId);
  const rps: Person[] = (cs.responsiblePartners || [])
    .map((_id: number) => co.find((c) => c.id === _id))
    .filter((p): p is Person => !!p);
  return (
    <div
      className="detail-card"
      style={{ flex: 1 }}
      data-secollapsed={sCol("info") ? "1" : "0"}
    >
      <h4
        className="csSecHdr"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 5,
          cursor: "pointer",
        }}
        onClick={() => tog("info")}
      >
        {!sCol("info") ? (
          <ChevronDown size={11} style={{ color: "var(--tm)" }} />
        ) : (
          <ChevronRight size={11} style={{ color: "var(--tm)" }} />
        )}
        {t("case.info")}
      </h4>
      <div className="detail-row">
        <span className="detail-label">
          {t("case.field.ref", { fallback: "Ref" })}
        </span>
        <span className="detail-value mono">{cs.ref}</span>
      </div>
      <div className="detail-row">
        <span className="detail-label">
          {t("case.field.status", { fallback: "Status" })}
        </span>
        <span className="detail-value">
          {/* BRIEF 86 PHASE 2 STEP 3 — a case with NO status painted a
              green "active" pill, because the class map falls back to
              `status-active` and `resolveTaxonomyLabel` of null is
              empty: an empty GREEN pill, asserting a status the row
              does not have.

              CollectiveCaseView already fixed this and says so in its
              own comment (55 row 6, `CollectiveCaseView.tsx:3992`) —
              "a row with no status gets no pill at all". CaseView was
              never told, the same shape as step 1's `canFin`. Found by
              reading the pair for the merge, fixed here rather than
              waiting for it, because a status the record does not hold
              is a wrong statement about a case.

              140 §1, the fourth site [RULED, Laurent, 2026-08-27 —
              found.txt item 2] — the pill census found FOUR sites
              rendering `span.status`, not three, and this field was
              the one §1 left untouched: a prospect read "Prospect" in
              the header and "Active" here, four sections down the same
              screen. Ruled the same as the other three: while the row
              is tagged this field shows the prospect pill IN PLACE of
              the status — the tag is tested first, so a tagged row
              never reaches the status lookup below. Same seeded
              `matters.prospect.badge` key, no new string. */}
          {cs.isProspect ? (
          <span className="status status-prospect">
            {t("matters.prospect.badge", { fallback: "Prospect" })}
          </span>
          ) : cs.status ? (
          <span
            className={`status ${statusClass(cs.status)}`}
          >
            {resolveTaxonomyLabel(cs.status, _caseStatusEntries)}
          </span>
          ) : (
            <span style={{ color: "var(--tm)" }}>—</span>
          )}
        </span>
      </div>
      {rps.length > 0 && (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "4px",
            flexWrap: "wrap",
            padding: "3px 0",
            fontSize: FS.s11_5,
            borderBottom: "1px solid rgba(255,255,255,.03)",
          }}
        >
          <span
            style={{
              color: "var(--tm)",
              fontSize: FS.s10_5,
              flexShrink: 0,
              marginRight: "auto",
            }}
          >
            {t("case.responsible_partner")}
          </span>
          {rps.map((_p) => (
            <CallAvatar
              key={_p.id}
              person={_p}
              isMe={_p.id === currentUser}
              onCall={onCallColleague}
              callActive={callActive}
              size={18}
              fontSize={8}
            />
          ))}
          {/* 75 §3 — every RP archived: the earmark. */}
          {soleArchived(rps) && (
            <ArchivedHoleBadge size={18} fontSize={8} style={{ marginLeft: 2 }} />
          )}
        </div>
      )}
      {(cs.caseCollaborators || []).length > 0 && (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "4px",
            flexWrap: "wrap",
            padding: "3px 0",
            fontSize: FS.s11_5,
            borderBottom: "1px solid rgba(255,255,255,.03)",
          }}
        >
          <span
            style={{
              color: "var(--tm)",
              fontSize: FS.s10_5,
              flexShrink: 0,
              marginRight: "auto",
            }}
          >
            {t("case.field.assigned", { fallback: "Assigned" })}
          </span>
          {/* Brief 48 §2 — RPs first and ringed. They are real team
              rows now (server/src/lib/caseTeam.js), so this list
              contains them; being in it sixth is not "clear". */}
          {teamWithRpFirst(
            cs.caseCollaborators,
            cs.responsiblePartners,
          ).map((cc: RankedTeamEntry) => {
            const c = co.find((x) => x.id === cc.coId);
            if (!c) return null;
            const isMe = c.id === currentUser;
            return (
              <CallAvatar
                key={cc.coId}
                person={c}
                isMe={isMe}
                onCall={onCallColleague}
                callActive={callActive}
                size={18}
                fontSize={8}
                {...(cc.isRp
                  ? {
                      style: RP_BADGE_STYLE,
                      title: t("case.responsible_partner"),
                    }
                  : {})}
              />
            );
          })}
          {/* 75 §3 — the whole team archived: the earmark. */}
          {soleArchived(
            teamWithRpFirst(cs.caseCollaborators, cs.responsiblePartners).map(
              (cc: RankedTeamEntry) => co.find((x) => x.id === cc.coId),
            ),
          ) && (
            <ArchivedHoleBadge size={18} fontSize={8} style={{ marginLeft: 2 }} />
          )}
        </div>
      )}
      <div className="detail-row">
        <span className="detail-label">{t("case.field.opened")}</span>
        <span className="detail-value mono">{fd(cs.openDate)}</span>
      </div>
      {/* 55 §3 — the other three of the four status dates. Opening is
          the row above. Each renders when it HOLDS a value, matching
          the modal's conditional inputs and the header chips rather
          than putting three empty rows under every active case. */}
      {cs.pendingDate && (
        <div className="detail-row">
          <span className="detail-label">{t("field.pending_since")}</span>
          <span className="detail-value mono">{fd(cs.pendingDate)}</span>
        </div>
      )}
      {cs.closedDate && (
        <div className="detail-row">
          <span className="detail-label">{t("field.closed_on")}</span>
          <span className="detail-value mono">{fd(cs.closedDate)}</span>
        </div>
      )}
      {cs.archivedDate && (
        <div className="detail-row">
          <span className="detail-label">{t("field.archived_on")}</span>
          <span className="detail-value mono">{fd(cs.archivedDate)}</span>
        </div>
      )}
      {(() => {
        const preEv = ag.find(
          (a: AgendaRow) => (isCc ? a.ccId === cs.id : a.caId === cs.id) && a.type === "PRE",
        );
        if (preEv) {
          const today = new Date();
          const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
          const evDate = preEv.date ? preEv.date.slice(0, 10) : "";
          const diff = evDate
            ? Math.round(
                (new Date(evDate).getTime() - new Date(todayStr).getTime()) /
                  (1000 * 60 * 60 * 24),
              )
            : null;
          const fmt = evDate
            ? evDate.split("-").reverse().join("/")
            : "—";
          const lbl =
            diff === null
              ? ""
              : diff === 0
                ? "today"
                : diff > 0
                  ? `in ${diff} day${diff === 1 ? "" : "s"}`
                  : `${Math.abs(diff)} day${Math.abs(diff) === 1 ? "" : "s"} ago`;
          /* [RULED 2026-08-17] THE PRESCRIPTION LADDER IS THE SAME ON
             BOTH KINDS: RED under 3 months · AMBER under 6 · GREEN
             beyond · grey once past. This is CollectiveCaseView's
             existing ladder and CaseView adopts it.

             TWO differences, not one. It reddened at 30 DAYS, so a
             deadline two months out was amber here and red there —
             the same date, two readings, on a limitation period. And
             it had NO colour beyond six months where CCV has green:
             "there is time" is worth STATING on a prescription
             deadline rather than left to the absence of a colour,
             which is indistinguishable from nothing having been
             computed. */
          const clr =
            diff === null
              ? undefined
              : diff < 0
                ? CT.c_888
                : diff < 90
                  ? "var(--rd)"
                  : diff < 183
                    ? "var(--am)"
                    : "var(--gn)";
          return (
            <div className="detail-row">
              <span className="detail-label">
                {t("case.field.prescription")}
              </span>
              <span
                className="detail-value"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                }}
              >
                <span
                  className="link mono"
                  style={{ cursor: "pointer" }}
                  /* BRIEF 86 PHASE 2 STEP 3 — `_caseFixed`, as CCV
                     has it. Editing a prescription deadline from
                     inside a matter should not let the matter itself
                     be changed by accident, and CaseView's own ADD
                     path pins it (six lines below) — so this was
                     inconsistent within one file as well as between
                     the two. */
                  onClick={() =>
                    openAgendaModal("ag", { ...preEv, _caseFixed: true })
                  }
                >
                  {fmt}
                </span>
                {diff !== null && (
                  <span
                    style={{
                      fontSize: FS.s10_5,
                      color: clr,
                      fontStyle: diff < 0 ? "italic" : "normal",
                    }}
                  >
                    {lbl}
                  </span>
                )}
              </span>
            </div>
          );
        }
        return (
          <div className="detail-row">
            <span className="detail-label">{t("case.field.prescription")}</span>
            <span
              className="detail-value"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <span
                style={{
                  color: "var(--rd)",
                  fontWeight: 700,
                  fontSize: FS.s13,
                }}
                title={t("case.no_prescription")}
              >
                ?
              </span>
              <span style={{ fontSize: FS.s10, color: "var(--tm)" }}>
                {t("case.no_deadline_set")}
              </span>
              <span
                className="link"
                style={{ fontSize: FS.s10, cursor: "pointer" }}
                onClick={() =>
                  /* BRIEF 86 PHASE 2 STEP 3 — the richer prefill,
                     CollectiveCaseView's. Opening this modal with only
                     a matter and a type left the date and the
                     attendees to be typed every time, on the one
                     affordance whose whole purpose is that a
                     prescription deadline gets recorded rather than
                     postponed. Less prefill is friction. */
                  openAgendaModal("ag", {
                    ...(isCc ? { ccId: cs.id } : { caId: cs.id }),
                    date: new Date().toISOString().slice(0, 10),
                    type: "PRE",
                    assignedCollaborators: (
                      cs.caseCollaborators || []
                    ).map((x: { coId: number }) => x.coId),
                    _caseFixed: true,
                  })
                }
              >
                {/* BRIEF 86 PHASE 2 STEP 3 — CCV's version, and it is
                    the considered one: brief 57 §2 ruled that a
                    create affordance opening a modal from a LINK is
                    not an AddButton, but shares
                    `common.add_button.add` with seven buttons that
                    are — those lost their "+" so the component could
                    own it, and this surface keeps its appearance by
                    emitting the character itself. CaseView was still
                    on `common.add`, never migrated. */}
                {"+ "}
                {t("common.add_button.add", { fallback: "Add" })}
              </span>
            </span>
          </div>
        );
      })()}
      <div className="detail-row">
        <span className="detail-label">
          {t("case.field.introductionDate", {
            fallback: "Introduction date",
          })}
        </span>
        <span className="detail-value mono">{fd(cs.introductionDate)}</span>
      </div>
      <div className="detail-row">
        <span className="detail-label">
          {t("case.field.original_jurisdiction")}
        </span>
        <span className="detail-value">
          {jr ? (
            <span
              className="link"
              style={{ color: "var(--tp)" }}
              onClick={() => nav("ju", jr.id)}
            >
              {jr.name}
            </span>
          ) : (
            "—"
          )}
        </span>
      </div>
      {(() => {
        const latest = getLatestRegStage(cs);
        const rn = latest ? latest.registerNumber || "" : "";
        const stName = latest ? latest.type || "?" : "?";
        const lblColor = rn ? undefined : "var(--rd)";
        return (
          <div className="detail-row">
            <span className="detail-label" style={{ color: lblColor }}>
              {t("case.field.current_register_n", { stName })}
            </span>
            <span className="detail-value">
              <span
                className="link"
                style={{ cursor: "pointer", color: lblColor }}
                onClick={() => {
                  const el = document.getElementById(isCc ? "cc-stages" : "ca-stages");
                  if (el)
                    el.scrollIntoView({
                      block: "start",
                      behavior: "smooth",
                    });
                }}
              >
                {rn || "?"}
              </span>
            </span>
          </div>
        );
      })()}
      <CaseFinancialPanel
        inv={inv}   /* brief 87 §3b — per-entry VAT */
        kind={isCc ? "cc" : "case"}
        cs={cs}
        te={te}
        co={co}
        canTerms={canTerms}
        canFin={canFin}
      />
    </div>
  );
}
